import { memo, useMemo } from 'react'
import { DocumentChip, DocumentPage } from '@/types/documents'
import { getChipsGroupFrameBounds } from '@/utils//image-zoom-pan'
import ChipFrame from './ChipFrame'
import { ProjectGridField } from '@/types/fields'
import { SelectedField } from '@/components/validation/providers/SelectedFieldProvider'

// Add a buffer zone around the viewport for smoother scrolling
const VIEWPORT_BUFFER_SIZE = 100

type VirtualizedChipGroupsProps = {
  chipGroups: DocumentChip[][]
  documentPage: DocumentPage
  fieldsMap: Record<string, ProjectGridField>
  selectedField: SelectedField | null
  isSelectedField: (fieldId: string, rowId: string) => boolean
  labelFontSize: number
  onFieldClick: (fieldId: string, rowId: string) => void
  viewportBounds?: { x: number; y: number; width: number; height: number }
}

const VirtualizedChipGroups = memo(
  ({
    chipGroups,
    documentPage,
    fieldsMap,
    selectedField,
    isSelectedField,
    labelFontSize,
    onFieldClick,
    viewportBounds,
  }: VirtualizedChipGroupsProps) => {
    // Pre-calculate bounds for faster filtering
    const chipGroupsBounds = useMemo(() => {
      return chipGroups.map((chips) => ({
        chips,
        bounds: getChipsGroupFrameBounds(
          chips,
          documentPage.width,
          documentPage.height,
        ),
      }))
    }, [chipGroups, documentPage.width, documentPage.height])

    // Only render chip groups that are visible or close to the viewport
    const visibleChipGroups = useMemo(() => {
      // Only proceed if we have valid viewport bounds
      if (!viewportBounds) return []

      // Calculate buffer size based on zoom level - use larger buffer at higher zoom levels
      const zoomFactor = labelFontSize > 20 ? 2 : 1
      const adjustedBufferSize = VIEWPORT_BUFFER_SIZE * zoomFactor

      // Add a safety margin to ensure we render everything visible or just outside the viewport
      const expandedBounds = {
        x: Math.max(0, viewportBounds.x - adjustedBufferSize),
        y: Math.max(0, viewportBounds.y - adjustedBufferSize),
        width: viewportBounds.width + adjustedBufferSize * 2,
        height: viewportBounds.height + adjustedBufferSize * 2,
      }

      // Apply intersection check to only render visible items
      // Use a more tolerant approach at high zoom levels
      const isHighZoom = labelFontSize > 20

      return chipGroupsBounds
        .filter(({ bounds }) => {
          // At high zoom levels, we need to be more inclusive to avoid filtering
          // out visible elements incorrectly
          if (isHighZoom) {
            // More tolerant check for high zoom levels - include elements that are
            // even partially near the viewport
            const boundsWithMargin = {
              x: bounds.x - 10,
              y: bounds.y - 10,
              width: bounds.width + 20,
              height: bounds.height + 20,
            }

            return (
              boundsWithMargin.x <= expandedBounds.x + expandedBounds.width &&
              boundsWithMargin.x + boundsWithMargin.width >= expandedBounds.x &&
              boundsWithMargin.y <= expandedBounds.y + expandedBounds.height &&
              boundsWithMargin.y + boundsWithMargin.height >= expandedBounds.y
            )
          }

          // Standard check for normal zoom levels
          return (
            bounds.x <= expandedBounds.x + expandedBounds.width &&
            bounds.x + bounds.width >= expandedBounds.x &&
            bounds.y <= expandedBounds.y + expandedBounds.height &&
            bounds.y + bounds.height >= expandedBounds.y
          )
        })
        .map((item) => item.chips)
    }, [chipGroupsBounds, viewportBounds, labelFontSize])

    return (
      <>
        {visibleChipGroups.map((chips) => {
          const fieldId = chips[0].project_grid_field_id as string
          const rowId = chips[0].document_row_id as string
          const isForSelectedField = selectedField
            ? isSelectedField(fieldId, rowId)
            : false

          return (
            <ChipFrame
              key={chips[0].id}
              chips={chips}
              documentPageWidth={documentPage.width}
              documentPageHeight={documentPage.height}
              fieldsMap={fieldsMap}
              isForSelectedField={isForSelectedField}
              labelFontSize={labelFontSize}
              onFieldClick={onFieldClick}
            />
          )
        })}
      </>
    )
  },
)

export default VirtualizedChipGroups
